import { useState, FormEvent } from "react";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d7cjbi4kpdh0a4advjr0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row rounded-3xl overflow-hidden">
          {/* Left Panel */}
          <div className="lg:w-2/5 bg-gray-900 p-10 md:p-14 flex flex-col justify-between">
            <div>
              <span className="text-teal-400 text-xs font-semibold tracking-widest uppercase mb-4 block">
                Get In Touch
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-6">
                Let&apos;s Solve Your<br />IT Challenges
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-10">
                Whether it&apos;s an urgent issue or a long-term project, our team is ready to help. Reach out and we&apos;ll get back to you within 2 hours.
              </p>

              <div className="flex flex-col gap-6">
                {[
                  { icon: "ri-phone-line", label: "Phone", value: "+1 (602) 492-7062" },
                  { icon: "ri-mail-line", label: "Email", value: "support@silentnet.net" },
                  { icon: "ri-map-pin-line", label: "Location", value: "Serving clients nationwide" },
                  { icon: "ri-time-line", label: "Hours", value: "24/7 Remote · Mon–Fri Onsite" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-500/15 text-teal-400 text-lg flex-shrink-0">
                      <i className={item.icon} />
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-0.5">{item.label}</div>
                      <div className="text-white text-sm font-medium">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Social */}
            <div className="flex gap-3 mt-10">
              {["ri-linkedin-box-line", "ri-twitter-x-line", "ri-facebook-box-line"].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-700 text-gray-400 hover:border-teal-500 hover:text-teal-400 transition-colors cursor-pointer"
                >
                  <i className={icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Right Panel: Form */}
          <div className="flex-1 bg-white p-10 md:p-14">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-teal-50 text-teal-500 text-3xl mb-5">
                  <i className="ri-checkbox-circle-line" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Message Sent!</h3>
                <p className="text-gray-500 text-sm max-w-xs">
                  Thanks for reaching out. Our team will get back to you within 2 hours.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-8">Request a Consultation</h3>
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Full Name
                      </label>
                      <input
                        name="name"
                        type="text"
                        required
                        placeholder="John Smith"
                        className="border-b-2 border-gray-200 focus:border-teal-500 outline-none py-2 text-sm text-gray-800 bg-transparent transition-colors"
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Email Address
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="john@company.com"
                        className="border-b-2 border-gray-200 focus:border-teal-500 outline-none py-2 text-sm text-gray-800 bg-transparent transition-colors"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Company
                      </label>
                      <input
                        name="company"
                        type="text"
                        placeholder="Your Company"
                        className="border-b-2 border-gray-200 focus:border-teal-500 outline-none py-2 text-sm text-gray-800 bg-transparent transition-colors"
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Service Needed
                      </label>
                      <select
                        name="service"
                        className="border-b-2 border-gray-200 focus:border-teal-500 outline-none py-2 text-sm text-gray-800 bg-transparent transition-colors cursor-pointer"
                      >
                        <option value="">Select a service...</option>
                        <option value="Remote Support">Remote Support</option>
                        <option value="Onsite Services">Onsite Services</option>
                        <option value="Cybersecurity">Cybersecurity</option>
                        <option value="Cloud Services">Cloud Services</option>
                        <option value="Network Infrastructure">Network Infrastructure</option>
                        <option value="Managed IT">Managed IT</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Message
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      placeholder="Describe your IT challenge or project..."
                      className="border-b-2 border-gray-200 focus:border-teal-500 outline-none py-2 text-sm text-gray-800 bg-transparent transition-colors resize-none"
                    />
                    <span className="text-xs text-gray-400 text-right">Max 500 characters</span>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-2 w-full py-3.5 rounded-full bg-teal-500 text-white font-semibold text-sm hover:bg-teal-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                  <p className="text-center text-xs text-gray-400">
                    We respond within 2 hours during business hours
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
